import React, { useEffect, useState } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';

import {
  enablementMetaKey,
  indexEnablementMeta,
  useEnablementItemMeta,
  useSaveEnablementItemMeta,
} from '../../../hooks/enablement-item-meta-hooks';

interface Props {
  mapId: number | null;
  mapName: string;
}

/**
 * The due date shown for this map on people's Enablement tables when their assignment has none. It saves
 * on its own, because it belongs to the map and not to the draft being edited.
 */
const JourneyMapDueDateField: React.FC<Props> = ({ mapId, mapName }) => {
  const { data } = useEnablementItemMeta(mapId != null);
  const existing = mapId == null ? undefined : indexEnablementMeta(data).get(enablementMetaKey('journey_map', mapId));
  const saved = existing?.due_date ? existing.due_date.slice(0, 10) : '';
  const [value, setValue] = useState(saved);
  const [message, setMessage] = useState('');
  const save = useSaveEnablementItemMeta();

  useEffect(() => setValue(saved), [saved]);

  const submit = (next: string) => {
    if (mapId == null) return;
    setMessage('');
    save.mutate(
      { existing, source: 'journey_map', source_id: String(mapId), name: mapName, due_date: next || null },
      {
        onSuccess: () => setMessage(next ? 'Due date saved.' : 'Due date cleared.'),
        onError: () => setMessage('Could not save the due date. Check your permissions and try again.'),
      },
    );
  };

  return (
    <Box>
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
        Due date
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Shown in the Due column of each person&apos;s Enablement table. A date set on an assignment takes priority.
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          size="small"
          type="date"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={mapId == null}
          inputProps={{ 'aria-label': 'Due date' }}
        />
        <Button size="small" variant="contained" disabled={mapId == null || value === saved || save.isPending} onClick={() => submit(value)}>
          Save
        </Button>
        <Button
          size="small"
          disabled={mapId == null || !saved || save.isPending}
          onClick={() => {
            setValue('');
            submit('');
          }}
        >
          Clear
        </Button>
      </Box>
      {mapId == null && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Save the map once to set a due date.
        </Typography>
      )}
      {message && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }} role="status">
          {message}
        </Typography>
      )}
    </Box>
  );
};

export default JourneyMapDueDateField;
